'use client';

import { cn } from '../../../../lib/utils';
import { CheckCircleFill, ErrorFill } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';
import { useEffect, useState } from 'react';

const requiredMarkSvg = () => {
  return (
    <svg
      width="9"
      height="8"
      viewBox="0 0 9 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.11048 7.13881L2.56091 4.66856L0 3.58074L0.453258 2.1983L3.1728 2.85552L3.4221 0H4.89518L5.14447 2.83286L7.86402 2.1983L8.31728 3.58074L5.75637 4.66856L7.22946 7.13881L6.05099 8L4.16997 5.8017L2.28895 8L1.11048 7.13881Z"
        className="fill-sys-cont-primary-default transition-all group-data-[custom-state=disabled]:fill-sys-cont-neutral-disabled-default"
      />
    </svg>
  );
};

const textFieldVariants = cva(
  [
    'flex items-center min-h-[48px] px-sm py-xs border rounded-sm bg-comp-text-field-bg-default hover:bg-comp-text-field-bg-hover transition-all',
  ],
  {
    variants: {
      state: {
        default:
          'border-comp-text-field-border-default focus-within:border-comp-text-field-border-focus focus-within:hover:bg-comp-text-field-bg-hover',
        error: 'border-comp-text-field-border-error',
        disabled: 'bg-comp-text-field-bg-disabled cursor-not-allowed',
        success: 'border-comp-text-field-border-success',
      },
      hasValue: {
        true: 'border-comp-text-field-border-focus group-data-[custom-state=error]:border-comp-text-field-border-error group-data-[custom-state=success]:border-comp-text-field-border-success',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

const inputVariants = cva(
  [
    'placeholder:transition-all transition-all',
    'flex w-full px-3xs outline-none text-comp-text-field-text-default caret-comp-text-field-caret placeholder:text-comp-text-field-placeholder',
  ],
  {
    variants: {
      state: {
        default: '',
        disabled:
          'cursor-not-allowed text-comp-text-field-text-disabled placeholder-comp-text-field-text-disabled pointer-events-none',
        error: '',
        success: '',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface TextFieldProps {
  /** 상태 */
  state?: 'default' | 'error' | 'disabled' | 'success';
  /** 레이블 표시 여부 */
  isShowLabel?: boolean;
  /** 필수 마크 표시 여부 */
  isShowRequiredMark?: boolean;
  /** 밑줄 표시 여부 */
  isShowBottomLine?: boolean;
  /** 헬퍼 텍스트 표시 여부 */
  isShowHelperText?: boolean;
  /** 헬퍼 텍스트 노드 */
  helperTextNode?: string | React.ReactNode;
  /** 문자 카운터 표시 여부 */
  isShowCharacterCounter?: boolean;
  /** 문자 카운터 노드 */
  characterNode?: React.ReactNode;
  /** 리드링크 아이콘 표시 여부 */
  isShowLeadingIcon?: boolean;
  /** 리드링크 아이콘 노드 */
  leadingIconNode?: React.ReactNode;
  /** 트레일링 노드 표시 여부 */
  isShowTrailingNode?: boolean;
  /** 트레일링 노드 컨테이너 className */
  trailingNodeContainerClassName?: HTMLDivElement['className'];
  /** 트레일링 앞 노드 */
  trailingForwardNode?: React.ReactNode;
  /** 트레일링 뒤 노드 */
  trailingBackwardNode?: React.ReactNode;
  /** 레이블 */
  label?: string;
  /** 최대 길이 표시 여부 */
  isMaxLength?: boolean;
  /** 최대 길이 */
  maxLength?: number | undefined;
  /** Input을 감싸고 있는 태그 클래스명 */
  className?: string;
}

export default function TextField({
  ...props
}: TextFieldProps & React.ComponentProps<'input'>) {
  const {
    state = 'default',
    isShowLabel,
    isShowTrailingNode,
    trailingNodeContainerClassName,
    trailingForwardNode,
    trailingBackwardNode,
    label,
    isShowBottomLine,
    isShowRequiredMark,
    isShowHelperText,
    helperTextNode,
    isShowCharacterCounter,
    characterNode,
    isShowLeadingIcon,
    leadingIconNode,
    maxLength,
    isMaxLength = false,
    className,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(
    rest.defaultValue || rest.value || '',
  );
  const [charCount, setCharCount] = useState(
    typeof inputValue === 'string' ? inputValue.length : 0,
  );

  useEffect(() => {
    if (typeof rest.value === 'string') {
      // 방어 코드: maxLength가 설정되어 있으면 입력값을 최대 길이로 제한
      const limitedValue =
        isMaxLength && maxLength !== undefined
          ? rest.value.slice(0, maxLength)
          : rest.value;

      setInputValue(limitedValue);
      setCharCount(limitedValue.length);
    }
  }, [isMaxLength, maxLength, rest.value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // disabled 상태일 때는 이벤트 처리를 하지 않음
    if (state === 'disabled') return;

    let newValue = e.target.value;

    // 방어 코드: maxLength가 설정되어 있으면 입력값을 최대 길이로 제한
    if (isMaxLength && maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);

      // input 요소의 값을 직접 업데이트하여 사용자 인터페이스에도 반영
      e.target.value = newValue;
    }

    setInputValue(newValue);
    setCharCount(newValue.length);

    if (rest.onChange) {
      rest.onChange(e);
    }
  };

  const renderCharacterCounter = (isShowHelperText?: boolean) => {
    if (characterNode) {
      return characterNode;
    }

    if (maxLength) {
      return (
        <span
          className={cn(
            'text-sys-cont-neutral-light transition-all group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            !isShowHelperText ? 'w-full text-right' : '',
          )}
        >
          {charCount}/{maxLength}
        </span>
      );
    }

    return null;
  };

  return (
    <div
      data-custom-state={state}
      className={cn('group flex flex-col gap-5xs', className)}
    >
      {isShowLabel && label && (
        <div className="text-field-label text-caption-small">
          <label
            htmlFor={label}
            className={cn(
              'flex items-center gap-5xs text-sys-cont-neutral-light transition-all',
              'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            )}
          >
            {label}
            {isShowRequiredMark && requiredMarkSvg()}
          </label>
        </div>
      )}
      <div
        className={cn(
          textFieldVariants({ state, hasValue: !!inputValue }),
          'input-text-field text-comp-moderate-medium',
        )}
      >
        {isShowLeadingIcon && leadingIconNode && (
          <div className="[&>svg]:size-[24px] [&>svg>g>path]:fill-sys-cont-neutral-default [&>svg>g>path]:transition-all group-data-[custom-state=disabled]:[&>svg>g>path]:fill-sys-cont-neutral-disabled-light">
            {leadingIconNode}
          </div>
        )}
        <input
          id={label}
          className={cn(inputVariants({ state }))}
          onChange={handleInputChange}
          maxLength={isMaxLength ? maxLength : undefined}
          autoComplete={rest.autoComplete || 'off'}
          disabled={state === 'disabled'}
          {...rest}
        />
        <div
          className={cn(
            'flex items-center gap-xs text-comp-moderate-small group-data-[custom-state=disabled]:pointer-events-none',
            trailingNodeContainerClassName,
          )}
        >
          <ErrorFill className="hidden size-[24px] group-data-[custom-state=error]:block [&>g>path]:fill-sys-style-negative-default" />
          <CheckCircleFill className="hidden size-[24px] group-data-[custom-state=success]:block [&>g>path]:fill-sys-cont-positive-default" />
          {isShowTrailingNode && (
            <>
              {trailingForwardNode && (
                <div className="[&_path]:fill-sys-cont-neutral-default [&_path]:transition-all group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light [&_svg]:size-[24px]">
                  {trailingForwardNode}
                </div>
              )}
              {trailingBackwardNode && (
                <div className="[&_path]:fill-sys-cont-neutral-default [&_path]:transition-all group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light [&_svg]:size-[24px]">
                  {trailingBackwardNode}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isShowBottomLine && (
        <div
          className={cn(
            'input-text-field-bottom-line flex items-center justify-between px-xs transition-all',
            'text-caption-small',
          )}
        >
          {isShowHelperText && (
            <div className="text-sys-cont-neutral-light transition-all group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light group-data-[custom-state=error]:text-sys-cont-negative-default">
              {helperTextNode}
            </div>
          )}
          {isShowCharacterCounter && renderCharacterCounter(isShowHelperText)}
        </div>
      )}
    </div>
  );
}
